// TransaccionDetails.tsx
import pagoTpl from '../../reports/Pago';

import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Heading, SimpleGrid, Text, Table, Thead, Tbody, Tfoot, Tr, Th, Td,
  VStack, HStack, Button, ButtonGroup, useToast, Tag, Code, Flex, Divider
} from "@chakra-ui/react";

// ==== Tipos ====
interface TipoDescuentoCat { id: number; descripcion: string; }

interface ConceptoTx {
  ID: number;
  HistorialID: number;
  ClaveProdServ: string;
  ClaveUnidad: string;
  Cantidad: number;
  NoIdentificacion: string;
  Unidad: string;
  Descuento: number;
  TipoDescuento: string; // llega como string
  Descripcion: string;
  ValorUnitario: number;
  Importe: number;
  ObjImp: string;
  ObjetoImp: string | null;
}

interface Transaccion {
  ID: number;
  RFCReceptor: string;
  usuario: string;
  Concepto: string | null;
  RazonSocial: string;
  MetodoPago: string;
  Monto: number;
  UUID: string | null;
  Fecha: string;
  f_pago: string;
  IDTransacc: string;
  ConceptosTransacciones: ConceptoTx[];
}

// ==== Catálogo Método de Pago (SAT) ====
const formasPagoSAT: Record<string, string> = {
  "01": "Efectivo",
  "02": "Cheque nominativo",
  "03": "Transferencia electrónica de fondos",
  "04": "Tarjeta de crédito",
  "28": "Tarjeta de débito",
};

// ==== Utilidades ====
const fmtMoney = (v: number) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v);

const fmtDate = (s?: string) =>
  s
    ? new Date(s).toLocaleString("es-MX", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "America/Mexico_City",
      })
    : "N/A";

const addDays = (d: Date, days: number) => {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + days);
  return nd;
};

// ===== Algoritmo 36 (BBVA CIE) =====
class CIE36 {
  static charToValue(ch: string): number {
    if (/[0-9]/.test(ch)) return ch.charCodeAt(0) - 48; // 0..9
    if (/[A-Z]/.test(ch)) {
      const pos = ch.charCodeAt(0) - 65; // 0..25
      return (pos % 9) + 1; // 1..9 cíclico
    }
    throw new Error(`Carácter no permitido: "${ch}"`);
  }
  static sumDigits(n: number): number {
    return n < 10 ? n : Math.floor(n / 10) + (n % 10);
  }
  static normalize(ref: string): string {
    const norm = ref.replace(/\s+/g, "").toUpperCase();
    if (norm.length < 2 || norm.length > 20) {
      throw new Error("La referencia debe tener entre 2 y 20 caracteres.");
    }
    if (!/^[A-Z0-9]+$/.test(norm)) {
      throw new Error("La referencia solo puede contener A-Z y 0-9.");
    }
    return norm;
  }
  static checkDigit(reference: string): number {
    const ref = this.normalize(reference);
    let sum = 0;
    let weight = 2;
    for (let i = ref.length - 1; i >= 0; i--) {
      const v = this.charToValue(ref[i]);
      sum += this.sumDigits(v * weight);
      weight = weight === 2 ? 1 : 2;
    }
    const residue = sum % 10;
    return (10 - residue) % 10; // si residue=0 ? 0
  }
  static build(reference: string): string {
    const ref = this.normalize(reference);
    return ref + this.checkDigit(ref).toString();
  }
}

// === Helpers de "atado" a conceptos, folio, monto y fecha ===

// 2 letras derivadas del primer concepto (A-Z), fallback "PD"
const conceptTag = (conceptos: ConceptoTx[]): string => {
  if (!conceptos?.length) return "PD";
  const src =
    (conceptos[0].Descripcion ||      // ?? primero usa la descripción
     conceptos[0].NoIdentificacion ||
     conceptos[0].ClaveProdServ ||
     "PD")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase()
      .replace(/[^A-Z]/g, "");
  const tag = (src.slice(0, 2) || "PD").padEnd(2, "P").slice(0, 2);
  return tag;
};

// Construye referencia base 17 chars: TT YY MM IIIIII CCCCC
// T: 2 letras de conceptos; Y: 2 dígitos año; M: 2 dígitos mes;
// I: últimos 6 dígitos del folio (ID); C: total en centavos (últimos 5 dígitos).
const buildReferenciaBase = (tx: Transaccion, total: number): string =>{
  const fecha = new Date(tx.Fecha || Date.now());
  const yy = String(fecha.getFullYear()).slice(-2);
  const mm = String(fecha.getMonth() + 1).padStart(2, "0");
  const id6 = String(Math.max(0, Number(tx.ID) || 0)).padStart(6, "0").slice(-6);
  const cents = Math.round((total ?? 0) * 100);
  const c5 = String(Math.max(0, cents)).padStart(5, "0").slice(-5);
  const tag = conceptTag(tx.ConceptosTransacciones); // 2 letras
  const base = `${tag}${yy}${mm}${id6}${c5}`; // 2 + 2 + 2 + 6 + 5 = 17
  // Validación defensiva
  if (base.length < 2 || base.length > 20) {
    throw new Error(`Referencia base fuera de rango (len=${base.length}): ${base}`);
  }
  return base;
}

// Reemplaza {{PLACEHOLDER}} en el template
const fillTemplate = (template: string, data: Record<string, string>) =>
  template.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (_, key) => data[key] ?? "");

// Inyecta filas en el <tbody> de la tabla de conceptos del template
const injectConceptRows = (template: string, rowsHtml: string) =>
  template.replace(/<tbody>[\s\S]*?<\/tbody>/, `<tbody>\n${rowsHtml}\n</tbody>`);

// Reemplaza contenedor de código de barras y agrega script JsBarcode
const ensureBarcode = (template: string, linea: string) => {
  let html = template.replace(
    /<div class="barcode">[\s\S]*?<\/div>/,
    `<div class="barcode"><svg id="barcode"></svg></div>`
  );

  const script =
    `
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
<script>
  (function() {
    var val = ${JSON.stringify(linea)};
    try {
      JsBarcode("#barcode", val, {
        format: "code128",
        lineColor: "#000",
        background: "transparent",
        width: 2,
        height: 50,
        displayValue: true,
        fontSize: 12,
        margin: 0
      });
    } catch(e) { console.error("Barcode error:", e); }
  })();
</script>
`.trim();

  html = html.replace(/<\/body>\s*<\/html>\s*$/i, `${script}\n</body>\n</html>`);
  return html;
};

// ==== Componente ====
const TransaccionDetails: React.FC<{ transaccionId: string }> = ({ transaccionId }) => {
  const [transaccion, setTransaccion] = useState<Transaccion | null>(null);
  const [tipoDescuentoCatalogo, setTipoDescuentoCatalogo] = useState<TipoDescuentoCat[]>([]);
  const [lineaCaptura, setLineaCaptura] = useState<string>("");
  const [timbrandoFactura, setTimbrandoFactura] = useState(false);
  const [cancelandoCFDI, setCancelandoCFDI] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/Transacciones/detalles/${transaccionId}`
        );
        const data = await response.json();
        setTransaccion(data);

        const cat = await fetch('https://facturacion-totolac.togui.com.mx/api/cTipoDescuentoRoutes').then((r) =>
          r.json()
        );
        setTipoDescuentoCatalogo(cat);
      } catch (error) {
        console.error("Error fetching transaccion:", error);
        toast({ status: "error", title: "No se pudo cargar la transacción" });
      }
    };
    load();
  }, [transaccionId, toast]);

  // Resumen de importes
  const resumen = useMemo(() => {
    if (!transaccion) return { subtotal: 0, total: 0, descuento: 0, recargos: 0 };
    const subtotal = transaccion.ConceptosTransacciones.reduce(
      (acc, c) => acc + c.ValorUnitario * c.Cantidad,
      0
    );
    const total = transaccion.ConceptosTransacciones.reduce((acc, c) => acc + c.Importe, 0);
    const descuento = Math.max(0, subtotal - total);
    const recargos = Math.max(0, total - subtotal);
    return { subtotal, total, descuento, recargos };
  }, [transaccion]);

  // Genera la REFERENCIA CIE36 y la devuelve junto con la base usada
  const generarReferenciaCIE36 = (tx: Transaccion, total: number) => {
    const base = buildReferenciaBase(tx, total);      // 17 chars atados a conceptos, folio, fecha, monto
    const referenciaFinal = CIE36.build(base);        // + DV Alg. 36 ? 18 chars
    // alert(base)
    // alert(referenciaFinal)
    return { base, referenciaFinal };
  };

  // Genera y guarda en estado la línea de captura (igual a la referencia final CIE36)
  const onGenerarLineaCaptura = () => {
    if (!transaccion) return;
    // const { referenciaFinal } = generarReferenciaCIE36(transaccion, resumen.total);
    const { referenciaFinal } = generarReferenciaCIE36(transaccion, 1);
    setLineaCaptura(referenciaFinal);
    toast({
      status: "success",
      title: "Línea de captura generada",
      description: referenciaFinal,
    });
  };

  // Función para timbrar la factura
  const onTimbrarFactura = async () => {
    if (!transaccion) return;

    setTimbrandoFactura(true);
    try {
      // Paso 1: Timbrar la factura (devuelve XML)
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/Facturacion/generarx-prod/${transaccion.ID}`
      );

      // El endpoint devuelve XML, no JSON
      const contentType = response.headers.get("content-type");

      if (contentType?.includes("application/xml")) {
        // Timbrado exitoso - devolvió XML
        toast({
          status: "success",
          title: "Factura timbrada exitosamente",
          description: "Descargando PDF...",
          duration: 3000,
          isClosable: true,
        });

        // Paso 2: Recargar la transacción para obtener el UUID actualizado
        const updatedResponse = await fetch(
          `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/Transacciones/detalles/${transaccionId}`
        );
        const updatedData = await updatedResponse.json();
        setTransaccion(updatedData);

        // Paso 3: Descargar PDF automáticamente
        const concepto = updatedData.Concepto || transaccion.Concepto;
        const isAgua = updatedData.IDTransacc?.startsWith("AP-");
        const imprimirEndpoint = isAgua
          ? `/api/Transacciones/ImprimirAgua/${transaccion.ID}`
          : `/api/Transacciones/Imprimir/${transaccion.ID}`;

        const pdfResponse = await fetch(
          `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}${imprimirEndpoint}`
        );

        if (pdfResponse.ok) {
          const blob = await pdfResponse.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Factura_${updatedData.UUID || transaccion.ID}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          toast({
            status: "success",
            title: "PDF descargado",
            description: `UUID: ${updatedData.UUID}`,
            duration: 5000,
            isClosable: true,
          });
        }
      } else if (contentType?.includes("application/json")) {
        // Error del PAC - devolvió JSON
        const data = await response.json();
        toast({
          status: "error",
          title: "Error al timbrar",
          description: data.MensajeError || "No se pudo timbrar la factura",
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error("Respuesta inesperada del servidor");
      }
    } catch (error) {
      console.error("Error al timbrar factura:", error);
      toast({
        status: "error",
        title: "Error de conexión",
        description: "No se pudo conectar con el servicio de timbrado",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setTimbrandoFactura(false);
    }
  };

  // Función para cancelar CFDI
  const onCancelarCFDI = async () => {
    if (!transaccion || !transaccion.UUID) {
      toast({
        status: "warning",
        title: "No se puede cancelar",
        description: "La factura no tiene UUID o no está timbrada",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Confirmación del usuario
    const confirmar = window.confirm(
      `¿Estás seguro de que deseas cancelar el CFDI?\n\nUUID: ${transaccion.UUID}\n\nEsta acción no se puede deshacer.`
    );

    if (!confirmar) return;

    setCancelandoCFDI(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/Facturacion/cancelar/${transaccion.UUID}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        toast({
          status: "success",
          title: "CFDI cancelado exitosamente",
          description: `UUID: ${transaccion.UUID}`,
          duration: 5000,
          isClosable: true,
        });

        // Recargar la transacción para reflejar el cambio
        const updatedResponse = await fetch(
          `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/Transacciones/detalles/${transaccionId}`
        );
        const updatedData = await updatedResponse.json();
        setTransaccion(updatedData);
      } else {
        toast({
          status: "error",
          title: "Error al cancelar CFDI",
          description: data.message || data.error || "No se pudo cancelar el CFDI",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error al cancelar CFDI:", error);
      toast({
        status: "error",
        title: "Error de conexión",
        description: "No se pudo conectar con el servicio de cancelación",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCancelandoCFDI(false);
    }
  };

  // Abrir formato Pago.html (importado como string) con datos + barcode
  const onVerFormato = () => {
    if (!transaccion) return;

    const emision = new Date(transaccion.Fecha || Date.now());
    const vigencia = addDays(emision, 5);

    // 1) Crea referencia final CIE36 (o usa la ya generada)
    const { referenciaFinal } = generarReferenciaCIE36(transaccion, resumen.total);
    const lc = lineaCaptura || referenciaFinal;

    // Mapa de placeholders para tu template
    const dataMap: Record<string, string> = {
      FOLIO: String(transaccion.ID),
      Folio: String(transaccion.ID),
      FECHA_EMISION: fmtDate(transaccion.Fecha),
      CONTRIBUYENTE: transaccion.RazonSocial || "-",
      RFC: transaccion.RFCReceptor || "-",
      DOMICILIO: "-",
      EMAIL: "-",
      PERIODO: String(emision.getFullYear()),
      VIGENCIA_PAGO: vigencia.toLocaleDateString("es-MX"),
      // SUBTOTAL: fmtMoney(resumen.subtotal),
      // RECARGOS: fmtMoney(resumen.recargos),
      // DESCUENTOS: fmtMoney(resumen.descuento),
      // TOTAL: fmtMoney(resumen.total),
       SUBTOTAL: '1',
      RECARGOS: '0',
      DESCUENTOS: '0',
      TOTAL: '1',
      LINEA_CAPTURA: lc,
      REFERENCIA: lc, // mostramos la referencia final como 'Referencia'
    };

    // 1) Rellenar placeholders
    let html = fillTemplate(pagoTpl, dataMap);

    // 2) Inyectar filas de conceptos
    const conceptRows = transaccion.ConceptosTransacciones
      .map((c) => {
        const concepto = c.NoIdentificacion || c.ClaveProdServ || "-";
        return `
          <tr>
            <td>${concepto}</td>
            <td class="muted">${c.Descripcion || "-"}</td>
            <td class="right">${fmtMoney(c.Importe)}</td>
          </tr>`;
      })
      .join("\n");
    html = injectConceptRows(html, conceptRows);

    // 3) Inyectar código de barras con la LC (Code128)
    html = ensureBarcode(html, lc);

    // 4) Abrir en una ventana nueva lista para imprimir
    const win = window.open("", "_blank");
    if (win) {
      win.document.open();
      win.document.write(html);
      win.document.close();

      // Espera a que cargue para imprimir con layout listo
      win.addEventListener("load", () => {
        win.requestAnimationFrame(() => {
          win.requestAnimationFrame(() => {
            try { win.focus(); } catch {}
            win.print();
            win.onafterprint = () => { try { win.close(); } catch {} };
          });
        });
      });
    } else {
      toast({ status: "warning", title: "Permite ventanas emergentes para ver el formato" });
    }
  };

  if (!transaccion) {
    return <Box>Cargando...</Box>;
  }

  // ==== UI ====
  return (
    <Box p={6} bg="white" borderRadius="md" boxShadow="md" maxW="1200px" mx="auto">
      {/* Header con acciones */}
      <Flex align="center" justify="space-between" mb={4} wrap="wrap" gap={3}>
        <VStack align="start" spacing={0}>
          <Heading as="h4" size="md">Detalle de Transacción</Heading>
          <HStack fontSize="sm" color="gray.600">
            <Text>Folio:</Text>
            <Code>{transaccion.ID}</Code>
            <Tag size="sm" colorScheme="purple">
              {formasPagoSAT[transaccion.f_pago] || transaccion.f_pago || "Método N/D"}
            </Tag>
          </HStack>
        </VStack>

        <ButtonGroup flexWrap="wrap">
          {!transaccion.UUID && (
            <Button
              colorScheme="green"
              onClick={onTimbrarFactura}
              isLoading={timbrandoFactura}
              loadingText="Timbrando..."
              leftIcon={<span>📄</span>}
            >
              Timbrar Factura
            </Button>
          )}
          {transaccion.UUID && (
            <Button
              colorScheme="red"
              onClick={onCancelarCFDI}
              isLoading={cancelandoCFDI}
              loadingText="Cancelando..."
              leftIcon={<span>❌</span>}
            >
              Cancelar CFDI
            </Button>
          )}
          <Button colorScheme="blue" onClick={onGenerarLineaCaptura}>
            Generar línea de captura (CIE36)
          </Button>
          <Button variant="outline" onClick={onVerFormato}>
            Ver formato / Imprimir
          </Button>
        </ButtonGroup>
      </Flex>

      <Divider mb={4} />

      {/* Identificador destacado */}
      <Box
        bgGradient="linear(to-r, teal.500, cyan.500)"
        color="white"
        borderRadius="xl"
        p={4}
        mb={6}
        boxShadow="lg"
      >
        <Flex align="center" justify="space-between">
          <VStack align="start" spacing={1}>
            <Text fontSize="sm" fontWeight="medium" opacity={0.9}>
              Identificador de Transacción
            </Text>
            <Text fontSize="3xl" fontWeight="bold">
              {transaccion.IDTransacc || "N/A"}
            </Text>
          </VStack>
          <VStack align="end" spacing={1}>
            <Text fontSize="sm" fontWeight="medium" opacity={0.9}>
              Folio
            </Text>
            <Text fontSize="2xl" fontWeight="bold">
              #{transaccion.ID}
            </Text>
          </VStack>
        </Flex>
      </Box>

      {/* Datos generales */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
        <Box>
          <Text fontWeight="bold">Razón Social</Text>
          <Text>{transaccion.RazonSocial}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">Método de Pago</Text>
          <Text>{formasPagoSAT[transaccion.f_pago] || transaccion.f_pago}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">RFC</Text>
          <Text>{transaccion.RFCReceptor}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">UUID</Text>
          {transaccion.UUID ? (
            <HStack>
              <Text fontSize="sm" color="green.600">{transaccion.UUID}</Text>
              <Tag size="sm" colorScheme="green">✓ Timbrado</Tag>
            </HStack>
          ) : (
            <HStack>
              <Text fontSize="sm" color="orange.600">Sin timbrar</Text>
              <Tag size="sm" colorScheme="orange">⏳ Pendiente</Tag>
            </HStack>
          )}
        </Box>
        <Box>
          <Text fontWeight="bold">Monto</Text>
          <Text>{fmtMoney(transaccion.Monto)}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">Fecha</Text>
          <Text>{fmtDate(transaccion.Fecha)}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold">Usuario</Text>
          <Text>{transaccion.usuario || "N/A"}</Text>
        </Box>
      </SimpleGrid>

      {/* Tabla de conceptos */}
      <Box borderRadius="md" boxShadow="sm" overflow="auto">
        <Heading as="h6" size="sm" p={4} pb={0}>
          Conceptos de Transacciones
        </Heading>
        <Table
          variant="simple"
          size="sm"
          sx={{
            "thead th": {
              position: "sticky",
              top: 0,
              bg: "gray.50",
              zIndex: 1,
            },
          }}
        >
          <Thead>
            <Tr>
              <Th>Clave Prod/Serv</Th>
              <Th>Descripción</Th>
              <Th isNumeric>Valor Unitario</Th>
              <Th isNumeric>Cantidad</Th>
              <Th>Unidad</Th>
              <Th isNumeric>Descuento</Th>
              <Th>Tipo Descuento</Th>
              <Th isNumeric>Subtotal</Th>
              <Th isNumeric>Total</Th>
            </Tr>
          </Thead>
          <Tbody>
            {transaccion.ConceptosTransacciones.map((c) => {
              const tipoDesc =
                tipoDescuentoCatalogo.find((t) => t.id === parseInt(c.TipoDescuento))
                  ?.descripcion || "N/A";
              const subtotal = c.Cantidad * c.ValorUnitario;
              return (
                <Tr key={c.ID}>
                  <Td><Code>{c.ClaveProdServ}</Code></Td>
                  <Td>{c.Descripcion}</Td>
                  <Td isNumeric>{fmtMoney(c.ValorUnitario)}</Td>
                  <Td isNumeric>{c.Cantidad}</Td>
                  <Td>{c.Unidad}</Td>
                  <Td isNumeric>{c.Descuento ? `${c.Descuento}%` : "-"}</Td>
                  <Td>{tipoDesc}</Td>
                  <Td isNumeric>{fmtMoney(subtotal)}</Td>
                  <Td isNumeric>{fmtMoney(c.Importe)}</Td>
                </Tr>
              );
            })}
          </Tbody>
          <Tfoot>
            <Tr>
              <Th colSpan={7} textAlign="right">
                Subtotal
              </Th>
              <Th isNumeric>{fmtMoney(resumen.subtotal)}</Th>
              <Th isNumeric>$</Th>
            </Tr>
            <Tr>
              <Th colSpan={7} textAlign="right">
                Descuentos
              </Th>
              <Th isNumeric>- {fmtMoney(resumen.descuento)}</Th>
              <Th isNumeric>$</Th>
            </Tr>
            <Tr>
              <Th colSpan={7} textAlign="right">
                Recargos / Actualización
              </Th>
              <Th isNumeric>{fmtMoney(resumen.recargos)}</Th>
              <Th isNumeric>$</Th>
            </Tr>
            <Tr>
              <Th colSpan={7} textAlign="right">
                TOTAL
              </Th>
              <Th isNumeric>{fmtMoney(resumen.total)}</Th>
              <Th isNumeric>$</Th>
            </Tr>
          </Tfoot>
        </Table>
      </Box>
    </Box>
  );
};

export default TransaccionDetails;
